import axios from 'axios';
import mimeType from 'mime-types';
import { ServiceDownload } from './service-download';
import type { ConversationMessage, SalesInboxItem } from '../ts-components/page-inbox/types';
import { inboxQueryParams } from './service-inbox';

export type SalesInboxQuery = {
  filter?: 'all' | 'unread';
  messageTypes?: string[];
  projectIds?: string[];
  q?: string;
  cursor?: string;
  limit?: number;
  instance?: string;   // page one instance only; omitted, the server merges every instance
};

export type SalesInboxPage = {
  items: SalesInboxItem[];
  hasMore: boolean;
  nextCursor?: string;
};

// A thread's own calls name the instance that holds it; the server routes them there.
const at = (instance?: string) => (instance ? { instance } : {});

// Names this instance on a list call, so the server pages it alone instead of every instance.
export const LOCAL_INSTANCE = 'local';

export const ServiceSalesInbox = {
  getInbox: async (options: SalesInboxQuery = {}): Promise<SalesInboxPage> => {
    const response = await axios.get('/api/sales/inbox', { params: { ...inboxQueryParams(options), ...at(options.instance) } });
    return response.data;
  },

  getCounter: async (): Promise<{ count: number }> => {
    const response = await axios.get('/api/sales/inbox/counter');
    return response.data;
  },

  markRead: async (conversationId: string, instance?: string) => {
    const response = await axios.put('/api/sales/inbox/mark-read', { conversationId, ...at(instance) });
    return response.data;
  },

  markUnread: async (conversationId: string, instance?: string) => {
    const response = await axios.put('/api/sales/inbox/mark-unread', { conversationId, ...at(instance) });
    return response.data;
  },

  markAllRead: async () => {
    const response = await axios.put('/api/sales/inbox/mark-all-read');
    return response.data;
  },

  getConversation: async (conversationId: string, instance?: string): Promise<{ conversation: ConversationMessage[] }> => {
    const response = await axios.get(`/api/sales/inbox/conversation/${conversationId}`, { params: at(instance) });
    return response.data;
  },

  downloadAttachment: (conversationId: string, file: string, fileName: string | undefined, instance?: string) => {
    const query = new URLSearchParams({ file });
    if (instance) query.set('instance', instance);
    const name = fileName || file;
    return ServiceDownload.downloadUrl(
      `/api/sales/inbox/conversation/${conversationId}/attachment?${query}`,
      name,
      mimeType.lookup(name) || 'application/octet-stream',
    );
  },

  postReply: async (conversationId: string, message: string, files: File[] = [], instance?: string) => {
    const formData = new FormData();
    formData.append('message', message);
    for (const file of files) formData.append('fileData', file);
    const response = await axios.put(`/api/sales/inbox/conversation/${conversationId}/message`, formData, { params: at(instance) });
    return response.data;
  },
};
